import type { ExistingBitriseApp } from '@/api/schemas';
import { GatedButton } from '@/components/GatedButton';
import { Button } from '@/components/ui/button';
import type { Can } from '@/lib/permissions';

function ExistingOption({ app, choice, onChoose }: { app: ExistingBitriseApp; choice: string; onChoose: (slug: string) => void }) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-[13px]">
      <input type="radio" name="bitrise-existing" value={app.slug} checked={choice === app.slug} onChange={() => onChoose(app.slug)} className="mt-1" />
      <span className="min-w-0">
        Use <span className="font-medium">{app.title ?? app.slug}</span> <span className="font-mono text-xs text-subtle">{app.slug}</span>
        <span className="block text-xs text-subtle">Its registration stays; Hangar stores the workflows on it, replacing a bitrise.yml kept in the repository.</span>
      </span>
    </label>
  );
}

interface Props {
  apps: ExistingBitriseApp[];
  choice: string;
  onChoose: (slug: string) => void;
  admin: Can;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Bitrise already has an app for the repository: use it, or create another one. */
export function BitriseExisting({ apps, choice, onChoose, admin, loading, onConfirm, onCancel }: Props) {
  return (
    <fieldset data-testid="bitrise-existing" className="flex flex-col gap-2 rounded-lg border bg-card p-3">
      <legend className="px-1 text-[13px] font-medium">Bitrise already has an app for this repository</legend>
      {apps.map((e) => (
        <ExistingOption key={e.slug} app={e} choice={choice} onChoose={onChoose} />
      ))}
      <label className="flex cursor-pointer items-start gap-2 text-[13px]">
        <input type="radio" name="bitrise-existing" value="create" checked={choice === 'create'} onChange={() => onChoose('create')} className="mt-1" />
        <span>Create another Bitrise app for it</span>
      </label>
      <div className="flex gap-2">
        <GatedButton size="sm" data-testid="bitrise-existing-confirm" can={admin} loading={loading} onClick={onConfirm}>
          Continue
        </GatedButton>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </fieldset>
  );
}
